import {
  ACCOUNT_COMMON_ARTICLES,
  AI_PREFERENCE_ARTICLES,
  ANALYTICS_ARTICLES,
  ASK_COPILOT_ARTICLES,
  BILLING_BASE_ARTICLES,
  DOCUMENT_BASE_ARTICLES,
  LANGUAGE_ARTICLES,
  MEETING_DEFAULT_ARTICLES,
  NOTIFICATION_ARTICLES,
} from '@copilot/help-content';

// Production Help & Support content for the global Help modal.
//
// This is intentionally static product documentation, not a user
// preference and not backend data. Keep wording aligned with the current
// UI labels/routes when a feature changes. Web-specific browser/audio
// mechanics live here; Desktop has its own platform-aware content file.
//
// HelpPanel searches section titles, questions, and answers, so common
// aliases (AirPods/headphones/response length/etc.) are written naturally
// into the relevant answer instead of introducing a second search system.

export const HELP_SECTIONS = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: 'play',
    items: [
      {
        id: 'dashboard-overview',
        question: 'What can I do from the Dashboard?',
        answer:
          'The Dashboard is your starting point for new meetings and recent activity. Use the main navigation for Meetings, Documents, Ask KrishvaQ, Analytics, Plan & Billing, and Settings.',
      },
      {
        id: 'start-a-meeting',
        question: 'Starting a meeting',
        answer:
          'Open New Meeting, enter a title, check that the Audio Source is Meeting / Browser Tab, then set Spoken Language, Translate To, AI Response Language, and KrishvaQ Mode. Meeting / Browser Tab asks you to share the browser tab that contains the call and include that tab\'s audio.',
      },
      {
        id: 'ending-a-meeting',
        question: 'Ending and saving a meeting',
        answer:
          'Select End Meeting from the live workspace when you are finished. The meeting is saved under Meetings, and any enabled post-meeting processing can continue after the live meeting closes.',
      },
    ],
  },
  {
    id: 'meetings',
    title: 'Meetings',
    icon: 'meeting',
    items: [
      {
        id: 'live-ai-assistance',
        question: 'Live transcription and AI assistance',
        answer:
          'The active meeting workspace shows live transcript activity and, when the selected KrishvaQ Mode includes AI, live AI answers. Transcript Only and Translation modes do not generate live AI answers.',
      },
      {
        id: 'meeting-history-details',
        question: 'Finding previous meetings and Meeting Details',
        answer:
          'Open Meetings to find previous meetings, then open one to view its details. Depending on what was enabled and successfully generated, Meeting Details can show Transcript, AI Answers, Meeting Summary and Decisions, and Action Items.',
      },
      {
        id: 'meeting-processing',
        question: 'Meeting summary is still processing or failed',
        answer:
          'Post-meeting analysis can take a short time after a meeting ends. If analysis fails, the summary area and notifications report the failure rather than showing a completed result; ending the meeting itself is still preserved.',
      },
      {
        id: 'summary-export',
        question: 'Exporting a meeting summary',
        answer:
          'When a Meeting Summary has completed successfully, its summary panel offers Export PDF. The export is not shown while the summary is pending, failed, or unavailable.',
      },
      {
        id: 'meeting-context',
        question: 'Using previous meetings or documents as meeting context',
        answer:
          'When starting a meeting, context controls can use the current meeting\'s attached documents and, depending on the selected context mode, relevant or selected previous meetings. This context helps live AI answers; it does not change the original transcript.',
      },
    ],
  },
  {
    id: 'meeting-audio',
    title: 'Meeting Audio',
    icon: 'mic',
    items: [
      {
        id: 'web-audio-source',
        question: 'What does Meeting / Browser Tab capture?',
        answer:
          'On Web, Meeting / Browser Tab is the Audio Source. It captures the audio of the browser tab you share (the other participants) and also uses your microphone, so your own speech is included in the same transcript.',
      },
      {
        id: 'microphone-permission',
        question: 'Microphone permission',
        answer:
          'Your browser must be allowed to use the microphone before Web can capture it or show reliable device names. If access was denied, open the site permissions for KrishvaQ in your browser, allow Microphone, then retry the meeting.',
      },
      {
        id: 'share-tab-audio',
        question: 'How do I share meeting audio from a browser tab?',
        answer:
          'Choose Meeting / Browser Tab, start the meeting, then select the tab containing Google Meet, Teams Web, Zoom Web, or another browser-based call. In the browser share dialog, enable the option to share tab audio before confirming.',
      },
      {
        id: 'no-transcription',
        question: 'Why is no transcription appearing?',
        answer:
          'Confirm the meeting is active, the microphone and tab-audio permissions were granted, and Spoken Language matches the conversation. If you selected Meeting / Browser Tab, make sure the shared tab still has an audio track and was not stopped in the browser.',
      },
      {
        id: 'web-bluetooth-audio',
        question: 'Can I use AirPods, Bluetooth headphones, or a headset on Web?',
        answer:
          'Yes, when your browser and operating system expose the device as an available microphone/output. Your microphone input is the one chosen under Settings > Audio Devices; the shared tab audio is captured separately, so changing headphones does not change what the tab shares.',
      },
    ],
  },
  {
    id: 'ai-preferences',
    title: 'AI & Preferences',
    icon: 'brain',
    items: [
      ...AI_PREFERENCE_ARTICLES,
      ...MEETING_DEFAULT_ARTICLES,
      {
        id: 'web-default-audio-source',
        question: 'Default Audio Source on Web',
        answer:
          'On Web, every meeting uses Meeting / Browser Tab as the Audio Source (it also uses your microphone). It is shown for information and cannot be changed; older settings that saved Microphone are ignored. This browser-specific default does not exist in the Desktop Meeting Defaults because Desktop uses Meeting Audio instead of a source-mode choice.',
      },
    ],
  },
  {
    id: 'languages',
    title: 'Languages',
    icon: 'languages',
    items: [...LANGUAGE_ARTICLES],
  },
  {
    id: 'documents',
    title: 'Documents',
    icon: 'file-text',
    items: [
      ...DOCUMENT_BASE_ARTICLES.slice(0, 2),
      {
        id: 'view-edit-documents',
        question: 'Viewing and editing documents',
        answer:
          'Open a document from the library to view supported content. Manual documents can be edited, and uploaded TXT content can be edited; other uploaded file formats are viewed from their processed/uploaded representation rather than rewritten in place.',
      },
      ...DOCUMENT_BASE_ARTICLES.slice(2),
    ],
  },
  {
    id: 'ask-copilot',
    title: 'Ask KrishvaQ',
    icon: 'search',
    items: [...ASK_COPILOT_ARTICLES],
  },
  {
    id: 'analytics',
    title: 'Analytics',
    items: [...ANALYTICS_ARTICLES],
  },
  {
    id: 'notifications',
    title: 'Notifications',
    icon: 'bell',
    items: [...NOTIFICATION_ARTICLES],
  },
  {
    id: 'account-security',
    title: 'Account & Security',
    icon: 'user',
    items: [
      ...ACCOUNT_COMMON_ARTICLES,
      {
        id: 'password-reset',
        question: 'Changing or resetting your password',
        answer:
          'If your account has a password, use Password & Sessions in Settings to change it. If you cannot sign in, use Forgot Password on the sign-in page and complete the reset from the email link.',
      },
      {
        id: 'sessions',
        question: 'Log out, session expiration, and Log Out All Devices',
        answer:
          'Normal Logout ends this browser session. If a session expires KrishvaQ tries to refresh it; if that fails you are returned to sign in. Log Out All Devices revokes every active session, including the one you are using.',
      },
      {
        id: 'legal',
        question: 'Privacy Policy and Terms of Service',
        answer:
          'Use the Legal links in Settings to open the existing Privacy Policy and Terms of Service. Help explains where to find them but does not duplicate the legal text.',
      },
    ],
  },
  {
    id: 'billing',
    title: 'Plan & Billing',
    icon: 'briefcase',
    items: [
      ...BILLING_BASE_ARTICLES.slice(0, 2),
      {
        id: 'change-plan',
        question: 'Changing your plan',
        answer:
          'Use Change Plan from Plan & Billing. Some plan changes can proceed through checkout immediately, while a change that requires administrator approval stays pending and your current plan remains in effect until the request is resolved.',
      },
      ...BILLING_BASE_ARTICLES.slice(2),
    ],
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    icon: 'help-circle',
    items: [
      {
        id: 'summary-missing',
        question: 'Where is my meeting summary?',
        answer:
          'Open the meeting and check Meeting Summary. A completed summary is unavailable while analysis is still pending, after analysis failure, or if post-meeting processing did not complete.',
      },
      {
        id: 'document-processing-failed',
        question: 'Why did document processing fail?',
        answer:
          'First confirm the file is one of the supported types and can be opened normally. A Failed status means extraction/processing did not complete; upload a valid copy again or create a manual document if you only need to provide text context.',
      },
      {
        id: 'signed-out',
        question: 'Why was I signed out?',
        answer:
          'You can be signed out when a session can no longer be refreshed, when Log Out All Devices is used, or when a security/device action revokes the session. Sign in again; if the event was unexpected, change your password and review account security.',
      },
      {
        id: 'support',
        question: 'How do I contact support?',
        answer:
          'Use Contact Support at the bottom of Help when a support email is configured for this build. The email template includes only basic non-sensitive Web context and leaves the issue details for you to fill in.',
      },
    ],
  },
];

export const FAQ_SECTION = {
  id: 'faq',
  title: 'FAQ',
  icon: 'question',
  items: [
    {
      id: 'faq-no-transcription',
      question: "Why isn't transcription appearing?",
      answer: 'Check the Meeting Audio section for browser permission, audio-source, shared-tab, and Spoken Language checks.',
    },
    {
      id: 'faq-mic-not-working',
      question: 'Why is my microphone not working?',
      answer: 'Allow microphone access for KrishvaQ in the browser and choose the intended microphone under Settings > Audio Devices.',
    },
    {
      id: 'faq-bluetooth',
      question: 'Can I use AirPods or Bluetooth headphones?',
      answer: 'Yes when your browser and operating system expose the device. See Meeting Audio for how your microphone and the shared tab audio are captured.',
    },
    {
      id: 'faq-change-language',
      question: 'How do I change my meeting language?',
      answer: 'Set the default in Settings > Language or override Spoken Language, Translate To, and AI Response Language when starting a meeting.',
    },
    {
      id: 'faq-ai-preferences',
      question: 'How do AI Personalization and Additional AI Settings work?',
      answer: 'AI Personalization sets how KrishvaQ answers new meetings (a built-in style or one of your Custom Profiles) once it is turned on for your account; each meeting keeps the style it started with. Additional AI Settings holds Answer Language, Meeting Type, question detection and the legacy Answer Style, Answer Length, Tone and Speaking Style, which shape answers only in meetings without an AI Response Style. The saved Language field does not currently override the meeting AI Response Language. These remain separate from Meeting Defaults.',
    },
    {
      id: 'faq-previous-meetings',
      question: 'Where can I find previous meetings?',
      answer: 'Open Meetings from the main navigation and select a meeting to open Meeting Details.',
    },
    {
      id: 'faq-summary',
      question: 'Where is my meeting summary?',
      answer: 'Open the meeting and select Meeting Summary. Processing must complete successfully before the finished summary and Export PDF action appear.',
    },
    {
      id: 'faq-document-processing',
      question: 'Why is my document still processing?',
      answer: 'Documents stay Queued or Processing while extraction runs. The library refreshes while processing is active and changes to Ready or Failed when it finishes.',
    },
    {
      id: 'faq-ask',
      question: 'How do I use Ask KrishvaQ?',
      answer: 'Open Ask KrishvaQ and ask a question about your saved meeting history. It searches meeting memory, not the general document library.',
    },
    {
      id: 'faq-notifications',
      question: 'Where can I see notifications?',
      answer: 'Use the bell in the application header. The badge shows unread items and the dropdown supports read, mark-all-read, navigation, and dismiss actions.',
    },
    {
      id: 'faq-password',
      question: 'How do I change my password?',
      answer: 'Open Settings > Password & Sessions. If you cannot sign in, use Forgot Password from the sign-in page.',
    },
    {
      id: 'faq-plan',
      question: 'Where can I see my plan?',
      answer: 'Open Plan & Billing from the main navigation for your plan, status, usage, dates, and payment history.',
    },
    {
      id: 'faq-cancel',
      question: 'How do I cancel my subscription?',
      answer: 'Use Cancel subscription in Plan & Billing when the action is available. The page shows when a scheduled cancellation takes effect.',
    },
    {
      id: 'faq-logout',
      question: 'How do I log out?',
      answer: 'Open your account menu in the top-right corner and select Logout.',
    },
    {
      id: 'faq-support',
      question: 'How do I contact support?',
      answer: 'Use Contact Support at the bottom of this Help window when your KrishvaQ build has a support email configured.',
    },
  ],
};

export const ALL_HELP_SECTIONS = [...HELP_SECTIONS, FAQ_SECTION];
